import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Activity, Clock, ClipboardCheck, Trophy, Users2, FileDown, FileSpreadsheet, FolderDown, Satellite, UserPlus, X, HeartPulse, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { exportTeamPdf, exportPlayerPdf, exportTeamXlsx, exportClubPdf, exportClubXlsx } from "@/lib/export";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import PlayerComparison from "@/components/PlayerComparison";
import RankingsCard from "@/components/RankingsCard";
import SigningAlertsCard from "@/components/SigningAlertsCard";
import SeasonView from "@/components/SeasonView";
import TeamDashboard from "@/components/TeamDashboard";
import TeamsSummary from "@/components/TeamsSummary";
import PlayerCombobox from "@/components/PlayerCombobox";
import PhysioCoachCard from "@/components/PhysioCoachCard";
import DashboardAlertDialog from "@/components/DashboardAlertDialog";

const currentSeasonYear = () => {
  const now = new Date();
  return now.getMonth() + 1 >= 7 ? now.getFullYear() : now.getFullYear() - 1;
};
const seasonLabel = (y) => `${y}/${String(y + 1).slice(2)}`;

const CHART_COLORS = ["#9333EA", "#EAB308", "#A855F7", "#C084FC", "#E9D5FF", "#6B21A8"];

const StatCard = ({ icon: Icon, label, value, color, testId, onClick }) => (
  <Card
    className="border-slate-200 shadow-sm hover:-translate-y-1 transition-transform cursor-pointer hover:shadow-md"
    data-testid={testId}
    onClick={onClick}
    role="button"
  >
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg grid place-items-center ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
      <div className="text-3xl font-black" style={{ fontFamily: "Nunito" }}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </CardContent>
  </Card>
);

function MedicalAlertsBanner({ user }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [detail, setDetail] = useState(false);
  useEffect(() => {
    api.get("/players/medical-alerts").then((r) => setItems(r.data || [])).catch(() => {});
  }, [user]);
  if (hidden || items.length === 0) return null;
  const color = { rojo: "text-rose-700", naranja: "text-orange-600", amarillo: "text-yellow-700" };
  const label = (p) =>
    p.days_left < 0 ? "CADUCADO" : p.days_left === 0 ? "caduca HOY" : `caduca en ${p.days_left} día${p.days_left !== 1 ? "s" : ""}`;
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="dashboard-medical-alerts">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-black text-rose-900" style={{ fontFamily: "Nunito" }}>
          <HeartPulse className="h-4 w-4" /> Reconocimientos médicos: {items.length} jugador{items.length !== 1 ? "es" : ""} con caducidad próxima
        </div>
        <button onClick={() => setHidden(true)} data-testid="medical-alerts-dismiss">
          <X className="h-4 w-4 text-rose-400 hover:text-rose-700" />
        </button>
      </div>
      <ul className="mt-2 space-y-1">
        {items.slice(0, 6).map((p) => (
          <li key={p.id} className="text-xs text-slate-700 truncate">
            • <b>{p.name}</b> ({p.team_name}) — <b className={color[p.level]}>{label(p)}</b> · {p.medical_expiry.split("-").reverse().join("/")}
          </li>
        ))}
        {items.length > 6 && <li className="text-xs text-slate-500">…y {items.length - 6} más</li>}
      </ul>
      <div className="flex items-center gap-2 mt-2">
        <Button size="sm" variant="outline" onClick={() => setDetail(true)}
          className="h-7 text-xs border-rose-300 text-rose-700 font-bold" data-testid="medical-alerts-view-all">
          Ver todo ({items.length})
        </Button>
        <Button size="sm" variant="ghost" onClick={() => navigate("/players")}
          className="h-7 text-xs text-rose-700 font-bold" data-testid="medical-alerts-goto">
          Ver jugadores →
        </Button>
      </div>
      <DashboardAlertDialog type="medical" open={detail} items={items} onItems={setItems}
        onClose={() => setDetail(false)} navigate={navigate} />
    </div>
  );
}

function MinutesReviewBanner({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ matches: [] });
  const [hidden, setHidden] = useState(false);
  const [detail, setDetail] = useState(false);
  useEffect(() => {
    if (!(user?.role === "admin" || user?.role === "coach")) return;
    api.get("/matches/minutes-review").then((r) => setData(r.data || { matches: [] })).catch(() => {});
  }, [user]);
  if (hidden || !data.matches || data.matches.length === 0) return null;
  const fmt = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
  const shown = detail ? data.matches : data.matches.slice(0, 5);
  return (
    <div className="rounded-xl border border-rose-300 bg-rose-50 p-4" data-testid="dashboard-minutes-review">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-black text-rose-900" style={{ fontFamily: "Nunito" }}>
          <AlertTriangle className="h-4 w-4" /> Minutos a revisar: {data.matches.length} partido{data.matches.length !== 1 ? "s" : ""} con minutos por encima del máximo (borrador, no cuentan)
        </div>
        <button onClick={() => setHidden(true)} data-testid="minutes-review-dismiss">
          <X className="h-4 w-4 text-rose-400 hover:text-rose-700" />
        </button>
      </div>
      {(data.by_team || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="minutes-review-by-team">
          {data.by_team.map((t) => (
            <button key={t.team_id} onClick={() => navigate(`/matches?team_id=${t.team_id}`)}
              className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-2 py-0.5 text-[11px] font-bold text-rose-800 hover:bg-rose-100"
              data-testid={`minutes-review-team-${t.team_id}`} title={t.category || ""}>
              {t.team_name}
              <span className="rounded-full bg-rose-600 text-white px-1.5">{t.count}</span>
              {t.red_count > 0 && <span className="text-[9px] text-rose-500">({t.red_count} grave{t.red_count !== 1 ? "s" : ""})</span>}
            </button>
          ))}
        </div>
      )}
      <ul className="mt-2 space-y-1">
        {shown.map((m) => (
          <li key={m.match_id} className="text-xs text-slate-700 flex items-center justify-between gap-3">
            <span className="truncate">
              • <b>{m.team_name}</b> vs {m.rival || "—"} · {fmt(m.date)} — {m.minutes_sum}/{m.minutes_max}'
              {m.red_alert && <span className="ml-1 text-[9px] bg-rose-600 text-white rounded px-1 py-0.5">+{m.over_pct}%</span>}
            </span>
            <button onClick={() => navigate(`/matches/${m.match_id}`)}
              className="shrink-0 font-black text-rose-800 hover:underline" data-testid={`minutes-review-goto-${m.match_id}`}>
              sobran {m.over_minutes}' →
            </button>
          </li>
        ))}
      </ul>
      {data.matches.length > 5 && (
        <Button size="sm" variant="outline" onClick={() => setDetail((v) => !v)}
          className="mt-2 h-7 text-xs border-rose-300 text-rose-700 font-bold" data-testid="minutes-review-toggle">
          {detail ? "Ver menos" : `Ver todo (${data.matches.length})`}
        </Button>
      )}
    </div>
  );
}

function AttendanceGapsBanner({ dateFrom, dateTo }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ teams: [], threshold: 4 });
  const [hidden, setHidden] = useState(false);
  const [detail, setDetail] = useState(false);
  const setTeams = (teams) => setData((d) => ({ ...d, teams }));
  useEffect(() => {
    api.get("/stats/attendance-gaps", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data || { teams: [] })).catch(() => {});
  }, [dateFrom, dateTo]);
  if (hidden || !data.teams || data.teams.length === 0) return null;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="dashboard-attendance-gaps">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-black text-amber-900" style={{ fontFamily: "Nunito" }}>
          <AlertTriangle className="h-4 w-4" /> Asistencia sin registrar: {data.teams.length} equipo{data.teams.length !== 1 ? "s" : ""} con más de {data.threshold} entrenamientos pendientes
        </div>
        <button onClick={() => setHidden(true)} data-testid="attendance-gaps-dismiss">
          <X className="h-4 w-4 text-amber-400 hover:text-amber-700" />
        </button>
      </div>
      <ul className="mt-2 space-y-1">
        {data.teams.slice(0, 6).map((t) => (
          <li key={t.team_id} className="text-xs text-slate-700 flex items-center justify-between gap-3">
            <span className="truncate">• <b>{t.team_name}</b>{t.category ? ` (${t.category})` : ""}</span>
            <button
              onClick={() => navigate(`/trainings?team_id=${t.team_id}`)}
              className="shrink-0 font-black text-amber-800 hover:underline"
              data-testid={`attendance-gap-goto-${t.team_id}`}
            >
              {t.count} sin registrar →
            </button>
          </li>
        ))}
        {data.teams.length > 6 && <li className="text-xs text-slate-500">…y {data.teams.length - 6} más</li>}
      </ul>
      <Button size="sm" variant="outline" onClick={() => setDetail(true)}
        className="mt-2 h-7 text-xs border-amber-400 text-amber-800 font-bold" data-testid="attendance-gaps-view-all">
        Ver todo ({data.teams.length})
      </Button>
      <DashboardAlertDialog type="attendance" open={detail} items={data.teams} onItems={setTeams}
        onClose={() => setDetail(false)} navigate={navigate} />
    </div>
  );
}


function SigningAlertsBanner({ user }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [detail, setDetail] = useState(false);
  const [fullItems, setFullItems] = useState([]);
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/scouting-market/players", { params: { sporting_status: "Fichar", sort: "score", page_size: 5 } })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {});
  }, [user]);
  const openAll = async () => {
    try {
      const r = await api.get("/scouting-market/players", { params: { sporting_status: "Fichar", sort: "score", page_size: 500 } });
      setFullItems(r.data.items || []);
    } catch (e) { setFullItems(items); }
    setDetail(true);
  };
  const onResolved = (next) => {
    setFullItems(next);
    setTotal(next.length);
    setItems(next.slice(0, 5));
  };
  if (user?.role !== "admin" || hidden || items.length === 0) return null;
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="dashboard-signing-alerts">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-black text-emerald-900" style={{ fontFamily: "Nunito" }}>
          <UserPlus className="h-4 w-4" /> Alertas de fichaje: {total} jugador{total !== 1 ? "es" : ""} marcado{total !== 1 ? "s" : ""} como "Fichar"
        </div>
        <button onClick={() => setHidden(true)} data-testid="signing-alerts-dismiss">
          <X className="h-4 w-4 text-emerald-400 hover:text-emerald-700" />
        </button>
      </div>
      <ul className="mt-2 space-y-1">
        {items.slice(0, 4).map((p) => (
          <li key={p.id} className="text-xs text-slate-700 truncate">
            • <b>{p.player_name}</b>{p.category ? ` · ${p.category}` : ""}{p.current_team ? ` · ${p.current_team}` : ""}{p.score != null ? ` · ${p.score} pts` : ""}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2 mt-2">
        <Button size="sm" variant="outline" onClick={openAll}
          className="h-7 text-xs border-emerald-300 text-emerald-700 font-bold" data-testid="signing-alerts-view-all">
          Ver todo ({total})
        </Button>
        <Button size="sm" variant="ghost" onClick={() => navigate("/scouting?tab=seguimiento&estado=Fichar")}
          className="h-7 text-xs text-emerald-700 font-bold" data-testid="signing-alerts-goto">
          Ver en Scouting →
        </Button>
      </div>
      <DashboardAlertDialog type="signing" open={detail} items={fullItems} onItems={onResolved}
        onClose={() => setDetail(false)} navigate={navigate} />
    </div>
  );
}

function RffmAlertsBanner({ user }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [detail, setDetail] = useState(false);
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/rffm/alerts").then((r) => setAlerts((r.data || []).filter((a) => !a.read))).catch(() => {});
  }, [user]);
  if (user?.role !== "admin" || hidden || alerts.length === 0) return null;
  const altas = alerts.filter((a) => a.type === "NUEVA_ALTA");
  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50 p-4" data-testid="dashboard-rffm-alerts">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-black text-purple-900" style={{ fontFamily: "Nunito" }}>
          <Satellite className="h-4 w-4" /> Motor RFFM: {alerts.length} alerta{alerts.length !== 1 ? "s" : ""} sin leer
          {altas.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px]">
              {altas.length} nueva{altas.length !== 1 ? "s" : ""} alta{altas.length !== 1 ? "s" : ""} rival
            </span>
          )}
        </div>
        <button onClick={() => setHidden(true)} data-testid="rffm-alerts-dismiss">
          <X className="h-4 w-4 text-purple-400 hover:text-purple-700" />
        </button>
      </div>
      <ul className="mt-2 space-y-1">
        {alerts.slice(0, 4).map((a) => (
          <li key={a.id} className="text-xs text-slate-700 truncate">• {a.message}</li>
        ))}
      </ul>
      <div className="flex items-center gap-2 mt-2">
        <Button size="sm" variant="outline" onClick={() => setDetail(true)}
          className="h-7 text-xs border-purple-300 text-purple-700 font-bold" data-testid="rffm-alerts-view-all">
          Ver todo ({alerts.length})
        </Button>
        <Button size="sm" variant="ghost" onClick={() => navigate("/rffm")}
          className="h-7 text-xs text-purple-700 font-bold" data-testid="rffm-alerts-goto">
          Ver en Motor RFFM →
        </Button>
      </div>
      <DashboardAlertDialog type="rffm" open={detail} items={alerts} onItems={setAlerts}
        onClose={() => setDetail(false)} navigate={navigate} />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [filters, setFilters] = useState({
    team_id: searchParams.get("team_id") || "all",
    player_id: "all",
    date_from: "", date_to: "", fase: "all",
  });
  const [season, setSeason] = useState(() => parseInt(searchParams.get("season") || currentSeasonYear(), 10));
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const seasonOptions = useMemo(() => {
    const cur = currentSeasonYear();
    return [cur, cur - 1, cur - 2, cur - 3];
  }, []);

  // Sincroniza los filtros con la URL (compartir/recargar sin perder la selección)
  useEffect(() => {
    const p = {};
    if (filters.team_id !== "all") p.team_id = filters.team_id;
    if (season !== currentSeasonYear()) p.season = String(season);
    setSearchParams(p, { replace: true });
  }, [filters.team_id, season, setSearchParams]);

  useEffect(() => {
    api.get("/teams").then((r) => {
      setTeams(r.data);
      // Los entrenadores con un solo equipo entran directamente a su dashboard
      if (user?.role === "coach" && r.data.length === 1 && !searchParams.get("team_id")) {
        setFilters((f) => ({ ...f, team_id: r.data[0].id }));
      }
    });
  }, []);

  useEffect(() => {
    if (filters.team_id === "all") {
      setPlayers([]);
      return;
    }
    api.get(`/players?team_id=${filters.team_id}`).then((r) => setPlayers(r.data));
  }, [filters.team_id, user]);

  useEffect(() => {
    if (filters.team_id !== "all") return; // el dashboard de equipo carga sus propios datos
    setLoading(true);
    const params = { date_from: `${season}-07-01`, date_to: `${season + 1}-06-30` };
    if (filters.fase !== "all") params.phase = filters.fase;
    api.get("/stats/dashboard", { params }).then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, [filters.team_id, filters.fase, season]);

  const openPlayerDashboard = (pid) => {
    if (!pid) return;
    const teamQ = filters.team_id !== "all" ? `team_id=${filters.team_id}&` : "";
    navigate(`/players/${pid}/dashboard?${teamQ}season=${season}`);
  };

  const set = (k) => (v) =>
    setFilters((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const totals = stats?.totals || {};
  const seasonRange = { date_from: `${season}-07-01`, date_to: `${season + 1}-06-30` };
  const navParams = () => {
    const p = new URLSearchParams();
    if (filters.team_id !== "all") p.set("team_id", filters.team_id);
    const s = p.toString();
    return s ? `?${s}` : "";
  };
  const teamQ = filters.team_id !== "all" ? `?team_id=${filters.team_id}` : "";
  const rows = useMemo(() => (stats?.by_player || []).sort((a, b) => b.minutes - a.minutes), [stats]);

  return (
    <div className="space-y-6">
      <RffmAlertsBanner user={user} />
      <MedicalAlertsBanner user={user} />
      <MinutesReviewBanner user={user} />
      <AttendanceGapsBanner dateFrom={`${season}-10-01`} dateTo={`${season + 1}-06-30`} />
      <SigningAlertsBanner user={user} />
      <PhysioCoachCard user={user} />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
            {user?.role === "admin" ? "Panel Director" : "Panel Entrenador"}
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: "Nunito" }} data-testid="dashboard-title">
            Hola, {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-slate-500 mt-1">Aquí tienes un resumen de la actividad.</p>
        </div>
        {filters.team_id !== "all" && (
          <>
            <Button
              variant="outline"
              onClick={() => {
                const team = teams.find((t) => t.id === filters.team_id);
                if (team) exportTeamPdf(team, seasonRange);
              }}
              data-testid="dashboard-export-team"
            >
              <FileDown className="h-4 w-4 mr-2" /> Equipo PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const team = teams.find((t) => t.id === filters.team_id);
                if (team) exportTeamXlsx(team, seasonRange);
              }}
              data-testid="dashboard-export-team-xlsx"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Equipo Excel
            </Button>
          </>
        )}
        {user?.role === "admin" && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => exportClubPdf(seasonRange)}
              data-testid="dashboard-export-club-pdf"
            >
              <FolderDown className="h-4 w-4 mr-2" /> Club PDF
            </Button>
            <Button
              variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => exportClubXlsx(seasonRange)}
              data-testid="dashboard-export-club-xlsx"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Club Excel
            </Button>
          </div>
        )}
      </div>

      {/* Filtros: temporada → equipo → jugador */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3 flex-wrap">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Temporada</Label>
            <Select value={String(season)} onValueChange={(v) => setSeason(parseInt(v, 10))}>
              <SelectTrigger className="w-32" data-testid="filter-season"><SelectValue /></SelectTrigger>
              <SelectContent>
                {seasonOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>{seasonLabel(y)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Equipo</Label>
            <Select value={filters.team_id} onValueChange={set("team_id")}>
              <SelectTrigger className="w-52" data-testid="filter-team">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(user?.role === "admin" || user?.role === "viewer") && (
                  <SelectItem value="all">Todos los equipos</SelectItem>
                )}
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Jugador</Label>
            <PlayerCombobox
              players={players}
              value=""
              onChange={openPlayerDashboard}
              placeholder={filters.team_id === "all" ? "Elige un equipo primero" : "Abrir dashboard del jugador…"}
              disabled={filters.team_id === "all"}
              testId="filter-player"
            />
          </div>
          {filters.team_id === "all" && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Fase</Label>
              <Select value={filters.fase} onValueChange={set("fase")}>
                <SelectTrigger className="w-40" data-testid="filter-phase">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Total temporada</SelectItem>
                  <SelectItem value="PRESEASON">Pretemporada</SelectItem>
                  <SelectItem value="COMPETITION">Competición</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {filters.team_id !== "all" ? (
        <TeamDashboard
          teamId={filters.team_id}
          season={season}
          canEdit={user?.role === "admin" || (user?.team_ids || []).includes(filters.team_id)}
        />
      ) : (
      <>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard icon={Users2} label="Jugadores" value={totals.players ?? 0} color="bg-purple-600" testId="stat-players" onClick={() => navigate(`/players${teamQ}`)} />
        <StatCard icon={ClipboardCheck} label="Entrenamientos" value={totals.trainings ?? 0} color="bg-sky-500" testId="stat-trainings" onClick={() => navigate(`/trainings${navParams()}`)} />
        <StatCard icon={Trophy} label="Partidos" value={totals.matches ?? 0} color="bg-amber-500" testId="stat-matches" onClick={() => navigate(`/matches${navParams()}`)} />
        <StatCard icon={Activity} label="% Asistencia" value={`${totals.attendance_pct ?? 0}%`} color="bg-purple-700" testId="stat-attendance" onClick={() => filters.team_id !== "all" ? navigate(`/teams/${filters.team_id}/detalle-stats?metric=attendance&scope=no_pre&season=${season}`) : navigate(`/trainings${navParams()}`)} />
        <StatCard icon={Clock} label="Minutos totales" value={totals.total_minutes ?? 0} color="bg-slate-800" testId="stat-minutes" onClick={() => navigate(`/matches${navParams()}`)} />
      </div>

      {user?.role === "admin" && (
        <>
          <SigningAlertsCard />
          <RankingsCard dateFrom={seasonRange.date_from} dateTo={seasonRange.date_to} />
        </>
      )}

      {/* Resumen por equipos — al pulsar un equipo se abre su dashboard concreto */}
      <TeamsSummary
        teams={teams}
        rows={rows}
        onSelect={(tid) => setFilters((f) => ({ ...f, team_id: tid }))}
      />

      {/* Season — match-by-match table with W/D/L badges and cumulative stats */}
      <SeasonView teams={teams} selectedTeamId={filters.team_id} />
      </>
      )}
    </div>
  );
}
